// Rest periods (Ruhezeiten), alarms (Wecker), standby screen and wake word of the
// resident's device. Same document the family and nursing apps edit; the
// backend validates it (backend/app/services/device_settings.py).
const API = process.env.REACT_APP_API_URL;

export const DAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
export const DAY_NAMES = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

export const ALARM_SOUNDS = [
  { id: "voegel", name: "Vogelgezwitscher", sub: "Leise und natürlich" },
  { id: "glocken", name: "Sanfte Glocken", sub: "Aufsteigende Töne" },
  { id: "klavier", name: "Morgenklavier", sub: "Ruhige Melodie" },
  { id: "gong", name: "Klangschale", sub: "Ein tiefer, warmer Ton" },
  { id: "meer", name: "Meeresrauschen", sub: "Wellen am Strand" },
  { id: "klassisch", name: "Klassischer Wecker", sub: "Deutlich hörbar" },
];

export const STANDBY_OPTIONS = [
  { id: "animation", name: "Schlafanimation" },
  { id: "clock", name: "Uhr" },
  { id: "photos", name: "Fotos der Familie" },
  { id: "off", name: "Bildschirm aus" },
];

// Must match OFF_AFTER_STEPS in the backend.
export const OFF_AFTER_STEPS = [5, 10, 15, 30, 45, 60, 90, 120, 180, 300];

export async function loadDeviceSettings(patientId) {
  const res = await fetch(`${API}/patients/${patientId}/device-settings`, { credentials: "include" });
  if (!res.ok) throw new Error("Geräteeinstellungen konnten nicht geladen werden.");
  return res.json();
}

export async function saveDeviceSettings(patientId, settings) {
  const res = await fetch(`${API}/patients/${patientId}/device-settings`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(settings),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error || "Geräteeinstellungen konnten nicht gespeichert werden.");
  }
  return res.json();
}

export const toMinutes = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

export const todayIndex = (now = new Date()) => (now.getDay() + 6) % 7;
export const newId = () => Date.now() % 2000000000;

export function formatDays(days) {
  const sorted = [...days].sort((a, b) => a - b);
  const key = sorted.join(",");
  if (sorted.length === 7) return "Täglich";
  if (key === "0,1,2,3,4") return "Mo – Fr";
  if (key === "5,6") return "Wochenende";
  if (!sorted.length) return "Einmalig";
  return sorted.map((d) => DAY_LABELS[d]).join(", ");
}

export function formatSeconds(s) {
  if (s < 60) return `${s} Sek.`;
  return s % 60 ? `${Math.floor(s / 60)} Min. ${s % 60} Sek.` : `${s / 60} Min.`;
}

export const soundName = (id) => ALARM_SOUNDS.find((s) => s.id === id).name;

export function quietSummary(q) {
  if (!q.enabled) return "Aus";
  const w = q.windows[0];
  if (!w) return "Kein Zeitfenster";
  return `${w.from} – ${w.to}${q.windows.length > 1 ? ` + ${q.windows.length - 1} weitere` : ""}`;
}

export function alarmSummary(alarms) {
  const on = alarms.filter((a) => a.on);
  if (!on.length) return alarms.length ? "Alle Wecker aus" : "Kein Wecker";
  return `${on[0].time} · ${formatDays(on[0].days)}${on.length > 1 ? ` + ${on.length - 1}` : ""}`;
}

export function standbySummary(s) {
  if (s.type === "clock") return `Uhr · ${s.face === "antik" ? "Antik" : "Modern"}`;
  if (s.type === "off") return `Aus nach ${formatSeconds(s.off_after_s)}`;
  return STANDBY_OPTIONS.find((o) => o.id === s.type).name;
}

export function nextAlarm(alarms, now = new Date()) {
  const current = todayIndex(now) * 1440 + now.getHours() * 60 + now.getMinutes();
  let best = null;
  alarms.filter((a) => a.on).forEach((alarm) => {
    (alarm.days.length ? alarm.days : [0, 1, 2, 3, 4, 5, 6]).forEach((day) => {
      let minutes = day * 1440 + toMinutes(alarm.time) - current;
      if (minutes <= 0) minutes += 7 * 1440;
      if (!best || minutes < best.minutes) best = { alarm, minutes };
    });
  });
  return best;
}

export function quietSegments(windows, day) {
  const segs = [];
  windows.forEach((w) => {
    const from = toMinutes(w.from);
    const to = toMinutes(w.to);
    if (w.days.includes(day)) segs.push([from, to > from ? to : 1440]);
    if (to <= from && w.days.includes((day + 6) % 7)) segs.push([0, to]);
  });
  return segs.sort((a, b) => a[0] - b[0]);
}
